import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { cn } from '../../lib/cn';

/** Ancho mínimo del desplegable, en píxeles. Debe coincidir con `min-w-[12rem]`. */
const ANCHO_MINIMO = 192;
/** Separación con el disparador y con el borde de la ventana. */
const MARGEN = 8;

interface Posicion {
  top: number;
  left: number;
  /** Alto máximo disponible; evita que el menú se salga por abajo. */
  maxHeight: number;
}

/**
 * Calcula dónde cae el desplegable en coordenadas de ventana.
 *
 * Se voltea hacia arriba cuando abajo no cabe, y se recorta al borde de la
 * ventana por los lados: un menú medio fuera de la pantalla no se puede usar,
 * y en el explorador de archivos las últimas filas están siempre pegadas al
 * borde inferior.
 */
function calcularPosicion(rect: DOMRect, align: 'left' | 'right'): Posicion {
  const anchoVentana = window.innerWidth;
  const altoVentana = window.innerHeight;

  const espacioAbajo = altoVentana - rect.bottom - MARGEN;
  const espacioArriba = rect.top - MARGEN;
  const haciaArriba = espacioAbajo < 160 && espacioArriba > espacioAbajo;

  const izquierdaCruda = align === 'right' ? rect.right - ANCHO_MINIMO : rect.left;
  const left = Math.max(MARGEN, Math.min(izquierdaCruda, anchoVentana - ANCHO_MINIMO - MARGEN));

  return haciaArriba
    ? { top: Math.max(MARGEN, rect.top - MARGEN), left, maxHeight: espacioArriba }
    : { top: rect.bottom + MARGEN, left, maxHeight: espacioAbajo };
}

/**
 * Menú desplegable.
 *
 * Implementa el patrón «menu button» de ARIA, no un `div` que aparece: con el
 * teclado se abre con Enter, Espacio o flecha, se recorre con ↑ ↓ Inicio Fin,
 * se cierra con Escape y **el foco vuelve al disparador**. Sin esa última
 * parte el foco queda en un elemento que ya no existe y el recorrido se
 * reinicia al principio de la página — que es como se pierde a alguien que
 * navega sin ratón.
 *
 * `kind` no es un adorno. `role="menu"` obliga a que todos sus hijos sean
 * `menuitem`; el panel de notificaciones contiene texto, enlaces y botones de
 * «marcar como leída», así que declararlo menú sería mentir sobre su forma y
 * un lector de pantalla anunciaría un menú de cero opciones. Los paneles se
 * recorren con Tab, como cualquier contenido.
 */
export function Dropdown({
  trigger,
  align = 'right',
  children,
  label,
  header,
  kind = 'menu',
}: {
  trigger: (props: { open: boolean }) => ReactNode;
  align?: 'left' | 'right';
  children: (props: { close: () => void }) => ReactNode;
  label: string;
  /**
   * Contenido informativo —un nombre, un correo— que se pinta **fuera** del
   * `role="menu"`. Meterlo dentro rompería la estructura del menú; dejarlo
   * fuera lo mantiene visible y legible sin fingir que es una opción.
   */
  header?: ReactNode;
  kind?: 'menu' | 'panel';
}) {
  const [open, setOpen] = useState(false);
  const [posicion, setPosicion] = useState<Posicion | null>(null);
  const contenedor = useRef<HTMLDivElement>(null);
  const disparador = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  /** Al abrir con ↑ se entra por la última opción, como en cualquier menú. */
  const entrarPorElFinal = useRef(false);
  /**
   * Queda pendiente meter el foco dentro.
   *
   * Hace falta una bandera porque el menú ya no se pinta en el mismo ciclo en
   * que se abre: primero se mide el disparador y sólo entonces existe el
   * desplegable. Enfocar al abrir apuntaría a algo que todavía no está.
   */
  const pendienteDeEnfocar = useRef(false);
  const menuId = useId();

  const esMenu = kind === 'menu';

  const cerrar = useCallback((devolverFoco: boolean) => {
    setOpen(false);
    if (devolverFoco) disparador.current?.focus();
  }, []);

  const opciones = useCallback(
    () =>
      Array.from(
        popup.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [],
      ),
    [],
  );

  /** Mueve el foco real entre opciones. Circular: del final vuelve al principio. */
  const mover = useCallback(
    (destino: number | 'primera' | 'ultima') => {
      const items = opciones();
      if (items.length === 0) return;

      const actual = items.indexOf(document.activeElement as HTMLElement);
      let siguiente: number;

      if (destino === 'primera') siguiente = 0;
      else if (destino === 'ultima') siguiente = items.length - 1;
      else if (actual < 0) siguiente = destino > 0 ? 0 : items.length - 1;
      else siguiente = (actual + destino + items.length) % items.length;

      items[siguiente]?.focus();
    },
    [opciones],
  );

  // Al abrir, el foco entra en el desplegable. Es lo que separa un menú
  // utilizable con teclado de uno que sólo responde al ratón.
  useEffect(() => {
    if (!open) {
      pendienteDeEnfocar.current = true;
      return;
    }
    // Sólo al entrar: sin la bandera, cada recolocación por desplazamiento
    // devolvería el foco a la primera opción mientras se recorre el menú.
    if (!posicion || !pendienteDeEnfocar.current) return;

    if (esMenu) mover(entrarPorElFinal.current ? 'ultima' : 'primera');
    else popup.current?.focus();

    pendienteDeEnfocar.current = false;
    entrarPorElFinal.current = false;
  }, [open, posicion, esMenu, mover]);

  /*
   * Posición en coordenadas de ventana.
   *
   * `useLayoutEffect` y no `useEffect` porque se mide antes de que el navegador
   * pinte: con `useEffect` el menú aparecería un fotograma en la esquina
   * superior izquierda y saltaría a su sitio.
   */
  useLayoutEffect(() => {
    if (!open) {
      setPosicion(null);
      return;
    }

    const medir = () => {
      const rect = disparador.current?.getBoundingClientRect();
      if (rect) setPosicion(calcularPosicion(rect, align));
    };

    medir();

    /*
     * Al desplazar o redimensionar hay que volver a medir, o el menú se queda
     * flotando donde estaba el botón. El `true` es captura: así se enteran
     * también los desplazamientos de contenedores internos, que no burbujean.
     */
    window.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('scroll', medir, true);
      window.removeEventListener('resize', medir);
    };
  }, [open, align]);

  // Clic fuera: se cierra sin robar el foco, porque el usuario ya lo ha
  // llevado a otro sitio a propósito.
  useEffect(() => {
    if (!open) return;

    const fuera = (event: MouseEvent) => {
      const destino = event.target as Node;
      // Se miran los dos: desde que el menú vive en un portal ya no está
      // dentro del contenedor, así que preguntar sólo por él cerraría el menú
      // al pulsar cualquiera de sus propias opciones.
      if (contenedor.current?.contains(destino) || popup.current?.contains(destino)) return;
      cerrar(false);
    };

    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [open, cerrar]);

  function teclaEnDisparador(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (!esMenu) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      entrarPorElFinal.current = event.key === 'ArrowUp';
      setOpen(true);
    }
  }

  function teclaEnPopup(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      cerrar(true);
      return;
    }

    // Tab cierra y devuelve el foco al disparador. Dejar que el navegador
    // tabule desde un elemento que se está desmontando manda el foco al
    // principio del documento, que es peor que no moverlo.
    if (event.key === 'Tab') {
      event.preventDefault();
      cerrar(true);
      return;
    }

    if (!esMenu) return;

    const salto: Record<string, number | 'primera' | 'ultima'> = {
      ArrowDown: 1,
      ArrowUp: -1,
      Home: 'primera',
      End: 'ultima',
    };

    const destino = salto[event.key];
    if (destino !== undefined) {
      event.preventDefault();
      mover(destino);
    }
  }

  return (
    <div className="relative" ref={contenedor}>
      <button
        ref={disparador}
        type="button"
        onClick={() => setOpen((actual) => !actual)}
        onKeyDown={teclaEnDisparador}
        aria-expanded={open}
        aria-haspopup={esMenu ? 'menu' : 'dialog'}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        className="rounded-xl"
      >
        {trigger({ open })}
      </button>

      {open && posicion
        ? createPortal(
            <div
              ref={popup}
              id={menuId}
              // Un panel recibe el foco al abrirse para que Escape funcione
              // desde el primer momento; un menú lo cede a su primera opción.
              tabIndex={esMenu ? undefined : -1}
              role={esMenu ? undefined : 'dialog'}
              aria-label={esMenu ? undefined : label}
              onKeyDown={teclaEnPopup}
              style={{
                top: posicion.top,
                left: posicion.left,
                maxHeight: Math.max(120, posicion.maxHeight),
              }}
              className={cn(
                'fixed z-50 min-w-[12rem] overflow-y-auto rounded-xl border border-line/60',
                'bg-surface shadow-card backdrop-blur-xl animate-slide-up',
              )}
            >
              {header ? <div className="border-b border-line/60 px-3 py-2">{header}</div> : null}

              {esMenu ? (
                <div role="menu" aria-label={label}>
                  {children({ close: () => cerrar(true) })}
                </div>
              ) : (
                children({ close: () => cerrar(true) })
              )}
            </div>,
            contenedorDelPortal(disparador.current),
          )
        : null}
    </div>
  );
}

/**
 * Dónde se cuelga el desplegable.
 *
 * Al `<body>`, salvo que el disparador viva dentro de un `<dialog>` abierto con
 * `showModal()`: ese diálogo se pinta en la capa superior del navegador, por
 * encima de todo lo demás, así que un menú colgado del `body` quedaría **detrás
 * del propio diálogo** y sería invisible.
 *
 * Sacarlo del árbol es justo lo que arregla el fallo original: dentro, cualquier
 * antepasado con `overflow: hidden` —como el contenedor de la lista de
 * archivos— recortaba el menú y lo dejaba inservible.
 */
function contenedorDelPortal(disparador: HTMLElement | null): HTMLElement {
  return disparador?.closest('dialog') ?? document.body;
}

export function DropdownItem({
  onClick,
  danger,
  disabled,
  children,
}: {
  onClick?: () => void;
  danger?: boolean;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      // Fuera del recorrido de Tab a propósito: dentro de un menú se navega
      // con flechas, y el propio menú es quien pone el foco.
      tabIndex={-1}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        // `min-h-[1.5rem]` cubre 2.5.8: ninguna opción por debajo de 24 px.
        'flex min-h-[1.5rem] w-full items-center gap-2 px-3 py-2 text-left text-sm transition',
        'hover:bg-surface-2 focus-visible:bg-surface-2',
        // El deshabilitado no se atenúa con opacidad: hunde el contraste hasta
        // hacerlo ilegible. Con `muted` sigue leyéndose y sigue distinguiéndose.
        'disabled:cursor-not-allowed disabled:text-muted disabled:hover:bg-transparent',
        danger ? 'text-danger' : 'text-content',
      )}
    >
      {children}
    </button>
  );
}

/** Separador entre grupos de opciones. `role="separator"` sí es válido en un menú. */
export function DropdownSeparator() {
  return <div role="separator" className="my-1 border-t border-line/60" />;
}
